import { QueryClient, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState } from 'react'

export type GnsUserState = GnsSubscribedUserState | null

export interface GnsSubscribedUserState {
  id?: string | null
  registrationTimestamp?: number | null
  subscriptionTimestamp?: number | null
  granted?: boolean
  grantReason?: string
}

export type GetCurrentGNSUserState = (queryClient: QueryClient) => GnsUserState

export const NoAccessUserState: GnsUserState = { granted: false }

const getCurrentGNSUserState: GetCurrentGNSUserState = (queryClient) =>
  queryClient.getQueryData<GnsUserState>(['gns-user-state']) ??
  NoAccessUserState

const useGnsUserState = () => {
  const queryClient = useQueryClient()
  const queryCache = queryClient.getQueryCache()

  const [gnsUserState, setGnsUserState] = useState<GnsUserState>(() =>
    getCurrentGNSUserState(queryClient)
  )

  const unsubscribeFnRef = useRef<() => void>(undefined)

  const updateGnsUserState = useCallback(
    (args: any) => {
      if (
        args?.action?.type === 'invalidate' &&
        args.query.queryKey[0] === 'gns-user-state'
      ) {
        setGnsUserState(getCurrentGNSUserState(queryClient))
      }
    },
    [queryClient]
  )

  useEffect(() => {
    unsubscribeFnRef.current = queryCache.subscribe(updateGnsUserState)

    return () => {
      if (unsubscribeFnRef.current) {
        unsubscribeFnRef.current()
      }
    }
  }, [updateGnsUserState, queryCache])

  return gnsUserState
}

export { getCurrentGNSUserState }

export default useGnsUserState
